/**
 * `BadgeTemplateService` — `/api/v1/events/{eventId}/badge-template`
 * плюс два рукописных маршрута для картинок.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 *  ⚠️ 404 НА ЧТЕНИИ — ЭТО НОРМАЛЬНОЕ СОСТОЯНИЕ, А НЕ ОШИБКА
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * У только что заведённого мероприятия шаблона нет: его никто не создаёт
 * автоматически, организатор выбирает заготовку. Поэтому `get` возвращает
 * `null` на `BADGE_TEMPLATE_NOT_FOUND` и пробрасывает всё остальное — иначе
 * каждый заход в новое мероприятие показывал бы красный тост про ошибку
 * сервера, которой не было.
 *
 * Разбор `reason` живёт здесь, а не в сторе, потому что это прочтение
 * КОНТРАКТА: «этот 404 означает пусто» — свойство эндпоинта, а не решение
 * экрана.
 *
 * ПРАВА: пишет admin или owner, **читает любой участник, включая наблюдателя**.
 * Печатает бейджи оператор аккредитации, а он viewer, и макет, который он не
 * может прочитать, — это бейдж, напечатанный пустым.
 */

import { http } from '@/lib/http'
import { ApiError, normalizeBlobError, normalizeError } from '@/lib/api-error'
import type {
  BadgeAsset,
  BadgeTemplate,
  DeleteBadgeTemplateResponse,
  GetBadgeTemplateResponse,
  SetBadgeTemplateRequest,
  SetBadgeTemplateResponse,
} from '@/types/api'

function path(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}/badge-template${suffix}`
}

function assetsPath(eventId: string, suffix = ''): string {
  return `/api/v1/events/${encodeURIComponent(eventId)}/badge-assets${suffix}`
}

export const badgeTemplateApi = {
  /**
   * `GET .../badge-template`. `null` — шаблона ещё нет.
   *
   * ⚠️ `null` и «ошибка» здесь разные вещи, и склеивать их нельзя: на первое
   * экран показывает галерею заготовок, на второе — сообщение с кнопкой
   * «Повторить». Отменённый запрос (`AbortController`) тоже НЕ превращается в
   * `null`: он пробрасывается, и вызывающий отличает его по `isCanceled`,
   * иначе гонка вкладок выглядела бы как «шаблон удалили».
   */
  async get(eventId: string, signal?: AbortSignal): Promise<BadgeTemplate | null> {
    try {
      const { data } = await http.get<GetBadgeTemplateResponse>(path(eventId), { signal })
      return data.badgeTemplate
    } catch (err) {
      const apiError = normalizeError(err)
      if (apiError.reason === 'BADGE_TEMPLATE_NOT_FOUND') return null
      throw apiError
    }
  },

  /**
   * `PUT .../badge-template` — записать макет ЦЕЛИКОМ. admin или owner.
   *
   * ⚠️ Ответ — НОРМАЛИЗОВАННЫЙ сервером шаблон, и класть в стор надо именно
   * его. Сервер подставляет умолчания (нулевая непрозрачность становится
   * единицей, нулевая тихая зона QR — четвёркой, цвет опускается в нижний
   * регистр), и если оставить в сторе отправленное, редактор будет показывать
   * одно, а печататься будет другое — расхождение, которое всплывёт на бумаге.
   */
  async set(eventId: string, body: SetBadgeTemplateRequest): Promise<BadgeTemplate> {
    const { data } = await http.put<SetBadgeTemplateResponse>(path(eventId), body)
    return data.badgeTemplate
  },

  /**
   * `DELETE .../badge-template` — вернуть мероприятие к «шаблона нет».
   *
   * ⚠️ Загруженные подложки и логотипы при этом НЕ удаляются: «начать заново»
   * не должно выбрасывать картинки, которые человек готовил полчаса.
   * Подтверждение обязано сказать это словами, иначе кнопка выглядит опаснее,
   * чем есть.
   */
  async remove(eventId: string): Promise<BadgeTemplate> {
    const { data } = await http.delete<DeleteBadgeTemplateResponse>(path(eventId))
    return data.badgeTemplate
  },

  /**
   * `POST .../badge-assets` — загрузить подложку или логотип. admin или owner.
   *
   * multipart с полем `file`, ровно как у импорта и у фотографии участника.
   *
   * ⚠️ `Content-Type` НЕ выставляется руками: его обязан собрать браузер
   * вместе с `boundary`, а перехватчик в `lib/http.ts` для `FormData` в тело не
   * лезет. Написать `'multipart/form-data'` самому — значит отправить запрос
   * без boundary, который сервер не разберёт.
   *
   * Сервер пересобирает картинку сам и сам решает формат по пикселям, поэтому
   * ответ несёт итоговый `contentType` и пиксельные габариты — по ним редактор
   * ставит логотип в его собственных пропорциях.
   */
  async uploadAsset(eventId: string, file: File, signal?: AbortSignal): Promise<BadgeAsset> {
    const form = new FormData()
    // Имя файла сервер не читает вовсе (расширение он ставит сам, по тому, что
    // сам же собрал), но FormData без имени шлёт поле как строку, а не как
    // файл, — и `FormFile` на той стороне его не находит. Та же строчка и по
    // той же причине стоит в `uploadPhoto`.
    form.append('file', file, file.name || 'badge-asset')

    const { data } = await http.post<BadgeAsset>(assetsPath(eventId), form, { signal })
    return data
  },

  /**
   * `GET .../badge-assets/{name}` — сами байты.
   *
   * ⚠️ Эндпоинт требует `Authorization: Bearer`, поэтому навести на него
   * `<img src>` нельзя: картинка молча превратилась бы в «битое изображение», в
   * котором лежит 401. Байты забираются axios'ом (с обновлением токена, единой
   * обработкой ошибок и отменой) — ровно как фотография участника.
   *
   * Принимает ПОЛНЫЙ ключ из шаблона и берёт из него только имя файла: событие
   * в пути запроса задаёт вызывающий, и оно авторитетнее того, что записано в
   * макете. Ключ чужого мероприятия сервер и так отвергает на записи — здесь
   * это вторая линия, чтобы неправильная строка не превращалась в запрос к
   * чужому скоупу.
   */
  async fetchAsset(eventId: string, keyOrName: string, signal?: AbortSignal): Promise<Blob> {
    const name = keyOrName.slice(keyOrName.lastIndexOf('/') + 1)
    if (!name) {
      throw new ApiError(404, 'BADGE_ASSET_NOT_FOUND')
    }

    try {
      const { data } = await http.get<Blob>(assetsPath(eventId, `/${encodeURIComponent(name)}`), {
        responseType: 'blob',
        signal,
      })
      return data
    } catch (err) {
      // ⚠️ `responseType: 'blob'` означает, что и ТЕЛО ОШИБКИ приезжает блобом:
      // перехватчик в `lib/http.ts` синхронный и разобрать его не может, а
      // без разбора «нет подложки» превратилось бы в безымянный 404 без
      // причины. Та же точечная обработка, что у фотографии участника.
      throw await normalizeBlobError(err)
    }
  },
}
